(function () {
  window.LeagueFormComponent = function LeagueFormComponent(props) {
    const {
      h,
      initialData,
      profiles,
      getStateAbbrev,
      getStateDisplayName,
      isGlobalCustomer,
      onClose,
      onSubmit,
      saveStatus,
    } = props;

    const hooks = window.preactHooks || {};
    const useEffect = hooks.useEffect;
    const useMemo = hooks.useMemo;
    const useState = hooks.useState;

    if (!useState || !useMemo || !useEffect) {
      return h("div", { className: "hd-empty" }, "Form unavailable");
    }

    const normalizeInternalStatus = function (value) {
      const normalized = String(value || "").trim().toLowerCase();
      if (normalized === "reviewed") return "verified";
      if (["pending_review", "verified", "certified", "deleted"].includes(normalized)) return normalized;
      return "pending_review";
    };

    const [step, setStep] = useState(1);
    const [locationQuery, setLocationQuery] = useState("");
    const [locationStateFilter, setLocationStateFilter] = useState("");
    const [locationCityFilter, setLocationCityFilter] = useState("");
    const [locationResults, setLocationResults] = useState(function () {
      return Array.isArray(profiles) ? profiles : [];
    });
    const [locationsLoading, setLocationsLoading] = useState(false);
    const [showStatusConfirm, setShowStatusConfirm] = useState(false);
    const [confirmStatusValue, setConfirmStatusValue] = useState(normalizeInternalStatus(initialData?.status));
    const [pendingUploads, setPendingUploads] = useState(0);
    const [uploadError, setUploadError] = useState("");
    const [divisionLocationQueries, setDivisionLocationQueries] = useState({});
    const [detailsTab, setDetailsTab] = useState("session");
    const [sessionOptions, setSessionOptions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [sessionError, setSessionError] = useState("");
    const [leagueTeams, setLeagueTeams] = useState([]);
    const [teamSearchQuery, setTeamSearchQuery] = useState("");
    const [teamNameInput, setTeamNameInput] = useState("");
    const [teamMembersInput, setTeamMembersInput] = useState("");
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [teamsError, setTeamsError] = useState("");
    const [creatingTeam, setCreatingTeam] = useState(false);
    const DAY_OPTIONS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const [galleryPreviews, setGalleryPreviews] = useState(function () {
      return Array.isArray(initialData?.galleryUrls) ? initialData.galleryUrls : [];
    });

    const normalizeRulesFile = function (value) {
      if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
      }
      const fileId = String(value.fileId || value.gid || "").trim();
      const url = String(value.url || "").trim();
      const fileName = String(value.fileName || value.name || "").trim();
      const mimeType = String(value.mimeType || value.type || "").trim();
      if (!fileId && !url && !fileName && !mimeType) {
        return {};
      }
      return {
        fileId: fileId || "",
        url: url || "",
        fileName: fileName || "",
        mimeType: mimeType || "",
      };
    };

    const buildInitialFormData = function () {
      const spec = initialData?.specificationData?.leagues || {};
      const defaults =
        initialData?.divisionDefaults && typeof initialData.divisionDefaults === "object"
          ? initialData.divisionDefaults
          : (spec.defaults || {});
      const activeSession = initialData?.activeSession && typeof initialData.activeSession === "object"
        ? initialData.activeSession
        : null;
      const divisionsSource = Array.isArray(initialData?.divisions)
        ? initialData.divisions
        : (Array.isArray(spec.divisions) ? spec.divisions : []);

      const normalizeDays = function (value) {
        const list = Array.isArray(value)
          ? value
          : String(value || "")
            .split(",")
            .map(function (part) {
              return part.trim();
            })
            .filter(Boolean);
        const normalized = list
          .map(function (day) {
            return String(day || "").trim().toLowerCase();
          })
          .filter(function (day) {
            return DAY_OPTIONS.includes(day);
          });
        return Array.from(new Set(normalized));
      };

      return {
        id: initialData?.id || "",
        title: initialData?.title || "",
        description: initialData?.description || "",
        isPublished: Boolean(initialData?.isPublished),
        status: normalizeInternalStatus(initialData?.status),
        contactName: initialData?.contactName || "",
        contactEmail: initialData?.contactEmail || "",
        contactPhone: initialData?.contactPhone || "",
        websiteUrl: initialData?.websiteUrl || "",
        profileTypes: Array.isArray(initialData?.profileTypes) ? initialData.profileTypes : [],
        annualMembershipFee:
          initialData?.annualMembershipFee === null || initialData?.annualMembershipFee === undefined
            ? ""
            : String(initialData.annualMembershipFee),
        matchFee:
          initialData?.matchFee === null || initialData?.matchFee === undefined ? "" : String(initialData.matchFee),
        defaultDescription: String(defaults?.description || initialData?.description || ""),
        defaultAnnualMembershipFee:
          defaults?.annualMembershipFee === null || defaults?.annualMembershipFee === undefined
            ? (initialData?.annualMembershipFee === null || initialData?.annualMembershipFee === undefined
              ? ""
              : String(initialData.annualMembershipFee))
            : String(defaults.annualMembershipFee),
        defaultMatchFee:
          defaults?.matchFee === null || defaults?.matchFee === undefined
            ? (initialData?.matchFee === null || initialData?.matchFee === undefined ? "" : String(initialData.matchFee))
            : String(defaults.matchFee),
        defaultDays: normalizeDays(defaults?.days),
        defaultStartTime: String(defaults?.startTime || ""),
        defaultEndTime: String(defaults?.endTime || ""),
        defaultRulesText: String(defaults?.rulesText || defaults?.rules_text || ""),
        defaultRulesFile: normalizeRulesFile(defaults?.rulesFile || defaults?.rules_file),
        sessionId: String(activeSession?.id || ""),
        sourceSessionId: "",
        sessionTitle: String(activeSession?.title || ""),
        sessionStatus: String(activeSession?.status || "planned"),
        sessionStartDate: String(activeSession?.startDate || "").slice(0, 10),
        sessionEndDate: String(activeSession?.endDate || "").slice(0, 10),
        divisions: divisionsSource.map(function (division, index) {
          return {
            id: String(division?.id || `division-${index + 1}`),
            name: String(division?.name || division?.title || ""),
            description: String(division?.description || defaults?.description || ""),
            rulesText: String(division?.rulesText || division?.rules_text || ""),
            rulesFile: normalizeRulesFile(division?.rulesFile || division?.rules_file),
            annualMembershipFee:
              division?.annualMembershipFee === null || division?.annualMembershipFee === undefined
                ? (division?.annual_membership_fee === null || division?.annual_membership_fee === undefined
                  ? ""
                  : String(division?.annual_membership_fee))
                : String(division?.annualMembershipFee),
            matchFee:
              division?.matchFee === null || division?.matchFee === undefined
                ? (division?.match_fee === null || division?.match_fee === undefined
                  ? ""
                  : String(division?.match_fee))
                : String(division?.matchFee),
            days: normalizeDays(division?.days),
            startTime: String(division?.startTime || division?.start_time || ""),
            endTime: String(division?.endTime || division?.end_time || ""),
            locationIds: Array.isArray(division?.locationIds)
              ? division.locationIds.map(function (id) { return String(id || ""); }).filter(Boolean)
              : (Array.isArray(division?.location_ids)
                ? division.location_ids.map(function (id) { return String(id || ""); }).filter(Boolean)
                : []),
            teamIds: Array.isArray(division?.teamIds)
              ? division.teamIds.map(function (id) { return String(id || ""); }).filter(Boolean)
              : (Array.isArray(division?.team_ids)
                ? division.team_ids.map(function (id) { return String(id || ""); }).filter(Boolean)
                : []),
            teams: Array.isArray(division?.teams) ? division.teams : [],
            teamMemberResults: Array.isArray(division?.teamMemberResults)
              ? division.teamMemberResults
              : (Array.isArray(division?.team_member_results) ? division.team_member_results : []),
            schedule: Array.isArray(division?.schedule) ? division.schedule : [],
          };
        }),
        gameTypes: Array.isArray(spec.game_types) ? spec.game_types : [],
        galleryFileIds: Array.isArray(initialData?.galleryFileIds) ? initialData.galleryFileIds : [],
        galleryUrls: Array.isArray(initialData?.galleryUrls) ? initialData.galleryUrls : [],
        locationIds: Array.isArray(initialData?.locations)
          ? initialData.locations.map(function (item) {
            return String(item?.profile?.id || "");
          }).filter(Boolean)
          : initialData?.profile?.id
            ? [String(initialData.profile.id)]
            : [],
      };
    };

    const [formData, setFormData] = useState(function () {
      return buildInitialFormData();
    });
    const isEditingLeague = Boolean(formData.id);
    const isSaving = /^saving\b/i.test(String(saveStatus || ""));

    useEffect(
      function () {
        setFormData(buildInitialFormData());
        setStep(1);
        setLocationQuery("");
        setLocationStateFilter("");
        setLocationCityFilter("");
        setLocationResults(Array.isArray(profiles) ? profiles : []);
        setLocationsLoading(false);
        setShowStatusConfirm(false);
        setConfirmStatusValue(normalizeInternalStatus(initialData?.status));
        setPendingUploads(0);
        setUploadError("");
        setDivisionLocationQueries({});
        setDetailsTab("session");
        setSessionOptions([]);
        setSessionsLoading(false);
        setSessionError("");
        setLeagueTeams([]);
        setTeamSearchQuery("");
        setTeamNameInput("");
        setTeamMembersInput("");
        setTeamsLoading(false);
        setTeamsError("");
        setCreatingTeam(false);
        setGalleryPreviews(Array.isArray(initialData?.galleryUrls) ? initialData.galleryUrls : []);
      },
      [initialData?.id, profiles]
    );

    const updateField = function (key, value) {
      setFormData(function (prev) {
        return { ...prev, [key]: value };
      });
    };

    useEffect(
      function () {
        const params = new URLSearchParams();
        params.set("app", "get-claimed-profiles");
        params.set("page", "1");
        params.set("pageSize", "100");
        if (locationQuery.trim().length >= 2) {
          params.set("q", locationQuery.trim());
        }
        if (locationStateFilter) {
          params.set("state", locationStateFilter);
        }
        if (locationCityFilter.trim()) {
          params.set("city", locationCityFilter.trim());
        }

        let cancelled = false;
        setLocationsLoading(true);

        fetch(`/a/app?${params.toString()}`, { method: "GET", cache: "no-store" })
          .then(function (response) {
            if (!response.ok) {
              throw new Error(`Request failed: ${response.status}`);
            }
            return response.json();
          })
          .then(function (data) {
            if (cancelled) return;
            const fetched = Array.isArray(data?.profiles) ? data.profiles : [];
            const selectedIds = new Set((formData.locationIds || []).map(function (id) {
              return String(id || "");
            }));
            const selectedProfiles = (profiles || []).filter(function (profile) {
              return selectedIds.has(String(profile?.id || ""));
            });
            const merged = new Map();
            selectedProfiles.concat(fetched).forEach(function (profile) {
              const profileId = String(profile?.id || "");
              if (!profileId) return;
              merged.set(profileId, profile);
            });
            setLocationResults(Array.from(merged.values()));
            setLocationsLoading(false);
          })
          .catch(function () {
            if (cancelled) return;
            // Fall back to already-known profile list if request fails.
            setLocationResults(Array.isArray(profiles) ? profiles : []);
            setLocationsLoading(false);
          });

        return function () {
          cancelled = true;
        };
      },
      [locationQuery, locationStateFilter, locationCityFilter, profiles]
    );

    const locationStateOptions = useMemo(function () {
      const values = new Set();
      (locationResults || []).forEach(function (profile) {
        const display = getStateDisplayName(profile?.locationState);
        if (display) values.add(display);
      });
      return Array.from(values).sort();
    }, [locationResults]);

    const locationCityOptions = useMemo(function () {
      const values = new Set();
      (locationResults || []).forEach(function (profile) {
        const city = String(profile?.locationCity || "").trim();
        if (city) values.add(city);
      });
      return Array.from(values).sort();
    }, [locationResults]);

    const filteredProfiles = useMemo(
      function () {
        return Array.isArray(locationResults) ? locationResults : [];
      },
      [locationResults]
    );

    const toggleLocation = function (profileId) {
      const id = String(profileId || "");
      if (!id) return;
      setFormData(function (prev) {
        const exists = prev.locationIds.includes(id);
        return {
          ...prev,
          locationIds: exists ? prev.locationIds.filter(function (value) { return value !== id; }) : prev.locationIds.concat(id),
        };
      });
    };

    const parseCsv = function (value) {
      return String(value || "")
        .split(",")
        .map(function (part) {
          return part.trim();
        })
        .filter(Boolean);
    };

    const addDivision = function () {
      const nextIndex = (Array.isArray(formData.divisions) ? formData.divisions.length : 0) + 1;
      const nextDivision = {
        id: `division-${Date.now()}-${nextIndex}`,
        name: "",
        description: formData.defaultDescription || "",
        rulesText: formData.defaultRulesText || "",
        rulesFile: normalizeRulesFile(formData.defaultRulesFile),
        annualMembershipFee: formData.defaultAnnualMembershipFee || "",
        matchFee: formData.defaultMatchFee || "",
        days: Array.isArray(formData.defaultDays) ? formData.defaultDays.slice() : [],
        startTime: formData.defaultStartTime || "",
        endTime: formData.defaultEndTime || "",
        locationIds: [],
        teamIds: [],
        teams: [],
        teamMemberResults: [],
        schedule: [],
      };
      updateField("divisions", (Array.isArray(formData.divisions) ? formData.divisions : []).concat(nextDivision));
    };

    const updateDivisionField = function (divisionIndex, key, value) {
      const divisions = Array.isArray(formData.divisions) ? formData.divisions.slice() : [];
      if (!divisions[divisionIndex]) return;
      divisions[divisionIndex] = {
        ...divisions[divisionIndex],
        [key]: value,
      };
      updateField("divisions", divisions);
    };

    const removeDivision = function (divisionIndex) {
      const divisions = Array.isArray(formData.divisions) ? formData.divisions.slice() : [];
      if (!divisions[divisionIndex]) return;
      divisions.splice(divisionIndex, 1);
      updateField("divisions", divisions);
    };

    const toggleDaySelection = function (fieldKey, dayValue) {
      const current = Array.isArray(formData[fieldKey]) ? formData[fieldKey] : [];
      const hasDay = current.includes(dayValue);
      updateField(
        fieldKey,
        hasDay ? current.filter(function (day) { return day !== dayValue; }) : current.concat(dayValue)
      );
    };

    const toggleDivisionDay = function (divisionIndex, dayValue) {
      const divisions = Array.isArray(formData.divisions) ? formData.divisions.slice() : [];
      if (!divisions[divisionIndex]) return;
      const current = Array.isArray(divisions[divisionIndex].days) ? divisions[divisionIndex].days : [];
      const hasDay = current.includes(dayValue);
      divisions[divisionIndex] = {
        ...divisions[divisionIndex],
        days: hasDay ? current.filter(function (day) { return day !== dayValue; }) : current.concat(dayValue),
      };
      updateField("divisions", divisions);
    };

    const setDivisionLocationQuery = function (divisionKey, value) {
      const key = String(divisionKey || "");
      setDivisionLocationQueries(function (prev) {
        return {
          ...prev,
          [key]: String(value || ""),
        };
      });
    };

    const toPrettyJson = function (value) {
      try {
        return JSON.stringify(Array.isArray(value) ? value : [], null, 2);
      } catch (_error) {
        return "[]";
      }
    };

    const parseJsonArray = function (value) {
      try {
        const parsed = JSON.parse(String(value || "[]"));
        return Array.isArray(parsed) ? parsed : [];
      } catch (_error) {
        return [];
      }
    };

    const parseTeamMembersInput = function (value) {
      return String(value || "")
        .split(",")
        .map(function (member) {
          return member.trim();
        })
        .filter(Boolean);
    };

    const fetchLeagueTeams = async function (leagueId, queryText) {
      const normalizedLeagueId = String(leagueId || "").trim();
      if (!normalizedLeagueId) return;

      setTeamsLoading(true);
      setTeamsError("");
      try {
        const params = new URLSearchParams();
        params.set("app", "get-league-teams");
        params.set("leagueId", normalizedLeagueId);
        params.set("perPage", "24");
        if (String(queryText || "").trim()) {
          params.set("q", String(queryText || "").trim());
        }

        const response = await fetch(`/a/app?${params.toString()}`, {
          method: "GET",
          cache: "no-store",
        });
        const payload = await response.json();
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.error || "Failed to load teams");
        }
        setLeagueTeams(Array.isArray(payload?.teams) ? payload.teams : []);
      } catch (error) {
        setTeamsError(String(error?.message || "Failed to load teams"));
      } finally {
        setTeamsLoading(false);
      }
    };

    useEffect(
      function () {
        if (!formData.id) return;
        fetchLeagueTeams(formData.id, teamSearchQuery);
      },
      [formData.id, teamSearchQuery]
    );

    const createLeagueTeam = async function () {
      const leagueId = String(formData.id || "").trim();
      const teamName = String(teamNameInput || "").trim();
      if (!leagueId) {
        setTeamsError("Save the league first before creating teams.");
        return;
      }
      if (!teamName) {
        setTeamsError("Team name is required.");
        return;
      }

      setCreatingTeam(true);
      setTeamsError("");
      try {
        const response = await fetch("/a/app?app=create-league-team", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            leagueId,
            name: teamName,
            members: parseTeamMembersInput(teamMembersInput),
          }),
        });
        const payload = await response.json();
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.error || "Failed to create team");
        }
        setTeamNameInput("");
        setTeamMembersInput("");
        await fetchLeagueTeams(leagueId, teamSearchQuery);
      } catch (error) {
        setTeamsError(String(error?.message || "Failed to create team"));
      } finally {
        setCreatingTeam(false);
      }
    };

    const toggleDivisionTeamId = function (divisionIndex, teamId) {
      const divisions = Array.isArray(formData.divisions) ? formData.divisions.slice() : [];
      if (!divisions[divisionIndex]) return;
      const normalizedTeamId = String(teamId || "").trim();
      if (!normalizedTeamId) return;
      const current = Array.isArray(divisions[divisionIndex].teamIds) ? divisions[divisionIndex].teamIds : [];
      const nextTeamIds = current.includes(normalizedTeamId)
        ? current.filter(function (value) { return value !== normalizedTeamId; })
        : current.concat(normalizedTeamId);
      divisions[divisionIndex] = {
        ...divisions[divisionIndex],
        teamIds: Array.from(new Set(nextTeamIds)),
      };
      updateField("divisions", divisions);
    };

    const toggleDivisionLocation = function (divisionIndex, profileId) {
      const divisions = Array.isArray(formData.divisions) ? formData.divisions.slice() : [];
      if (!divisions[divisionIndex]) return;
      const id = String(profileId || "").trim();
      if (!id) return;
      const current = Array.isArray(divisions[divisionIndex].locationIds) ? divisions[divisionIndex].locationIds : [];
      const nextIds = current.includes(id)
        ? current.filter(function (value) { return value !== id; })
        : current.concat(id);
      divisions[divisionIndex] = {
        ...divisions[divisionIndex],
        locationIds: Array.from(new Set(nextIds)),
      };
      updateField("divisions", divisions);
    };

    const resolveShopDomain = function () {
      try {
        const url = new URL(window.location.href);
        const fromQuery = String(url.searchParams.get("shop") || "").trim();
        if (fromQuery) return fromQuery;
      } catch (_error) {
        // ignore
      }
      const fromShopifyGlobal = String((window.Shopify && window.Shopify.shop) || "").trim();
      if (fromShopifyGlobal) return fromShopifyGlobal;
      return "";
    };

    const uploadSingleFileToShopify = async function (file) {
      const fileToDataUrl = function (nextFile) {
        return new Promise(function (resolve, reject) {
          const reader = new FileReader();
          reader.onload = function (e) {
            resolve(String(e?.target?.result || ""));
          };
          reader.onerror = function () {
            reject(new Error("Failed to read file"));
          };
          reader.readAsDataURL(nextFile);
        });
      };

      const previewUrl = await fileToDataUrl(file);
      const fileBase64 = previewUrl.includes(",") ? previewUrl.split(",").pop() : previewUrl;
      const shopDomain = resolveShopDomain();
      const query = new URLSearchParams({ app: "uploadFileToShopify" });
      if (shopDomain) query.set("shop", shopDomain);

      const response = await fetch(`/a/app?${query.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileBase64: String(fileBase64 || ""),
          fileName: String(file?.name || "upload-file"),
          mimeType: String(file?.type || "application/octet-stream"),
          shopDomain: shopDomain || undefined,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(String(payload?.message || payload?.error || "Upload failed"));
      }

      const gid = String(payload?.gid || payload?.fileId || "").trim();
      if (!gid) {
        throw new Error("Upload succeeded but no file ID was returned");
      }

      return {
        fileId: gid,
        url: String(payload?.url || "").trim(),
        fileName: String(payload?.fileName || file?.name || "").trim(),
        mimeType: String(payload?.mimeType || file?.type || "").trim(),
      };
    };

    const uploadRulesFile = async function (setter) {
      return async function (event) {
        const file = event?.target?.files?.[0];
        event.target.value = "";
        if (!file) return;
        setUploadError("");
        setPendingUploads(function (value) {
          return value + 1;
        });
        try {
          const uploaded = await uploadSingleFileToShopify(file);
          setter(uploaded);
        } catch (error) {
          setUploadError(String(error?.message || "Failed to upload rules file"));
        } finally {
          setPendingUploads(function (value) {
            return Math.max(0, value - 1);
          });
        }
      };
    };

    const loadSessionDetail = async function (sessionId) {
      const normalizedId = String(sessionId || "").trim();
      if (!normalizedId) return;

      const params = new URLSearchParams();
      params.set("app", "get-league-session-detail");
      params.set("sessionId", normalizedId);

      const response = await fetch(`/a/app?${params.toString()}`, {
        method: "GET",
        cache: "no-store",
      });
      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || "Failed to load session detail");
      }

      const session = payload?.session || {};
      const defaults = payload?.divisionDefaults && typeof payload.divisionDefaults === "object"
        ? payload.divisionDefaults
        : {};
      const divisions = Array.isArray(payload?.divisions) ? payload.divisions : [];

      setFormData(function (prev) {
        return {
          ...prev,
          sessionId: String(session?.id || normalizedId),
          sourceSessionId: "",
          sessionTitle: String(session?.title || prev.sessionTitle || ""),
          sessionStatus: String(session?.status || prev.sessionStatus || "planned"),
          sessionStartDate: String(session?.startDate || "").slice(0, 10) || prev.sessionStartDate,
          sessionEndDate: String(session?.endDate || "").slice(0, 10) || prev.sessionEndDate,
          defaultDescription: String(defaults?.description || ""),
          defaultAnnualMembershipFee:
            defaults?.annualMembershipFee === null || defaults?.annualMembershipFee === undefined
              ? ""
              : String(defaults?.annualMembershipFee),
          defaultMatchFee:
            defaults?.matchFee === null || defaults?.matchFee === undefined
              ? ""
              : String(defaults?.matchFee),
          defaultDays: Array.isArray(defaults?.days)
            ? defaults.days.map(function (day) { return String(day || "").trim().toLowerCase(); }).filter(Boolean)
            : [],
          defaultStartTime: String(defaults?.startTime || ""),
          defaultEndTime: String(defaults?.endTime || ""),
          defaultRulesText: String(defaults?.rulesText || defaults?.rules_text || ""),
          defaultRulesFile: normalizeRulesFile(defaults?.rulesFile || defaults?.rules_file),
          divisions: divisions.map(function (division, index) {
            return {
              id: String(division?.divisionKey || division?.id || `division-${index + 1}`),
              name: String(division?.name || ""),
              description: String(division?.description || ""),
              rulesText: String(division?.rulesText || division?.rules_text || ""),
              rulesFile: normalizeRulesFile(division?.rulesFile || division?.rules_file),
              annualMembershipFee:
                division?.annualMembershipFee === null || division?.annualMembershipFee === undefined
                  ? ""
                  : String(division?.annualMembershipFee),
              matchFee:
                division?.matchFee === null || division?.matchFee === undefined
                  ? ""
                  : String(division?.matchFee),
              days: Array.isArray(division?.days)
                ? division.days.map(function (day) { return String(day || "").trim().toLowerCase(); }).filter(Boolean)
                : [],
              startTime: String(division?.startTime || ""),
              endTime: String(division?.endTime || ""),
              locationIds: Array.isArray(division?.locationIds)
                ? division.locationIds.map(function (id) { return String(id || "").trim(); }).filter(Boolean)
                : [],
              teamIds: Array.isArray(division?.teamIds)
                ? division.teamIds.map(function (id) { return String(id || "").trim(); }).filter(Boolean)
                : [],
            };
          }),
        };
      });
    };

    const fetchLeagueSessions = async function (leagueId) {
      const normalizedLeagueId = String(leagueId || "").trim();
      if (!normalizedLeagueId) return;

      setSessionsLoading(true);
      setSessionError("");
      try {
        const params = new URLSearchParams();
        params.set("app", "get-league-sessions");
        params.set("leagueId", normalizedLeagueId);
        params.set("page", "1");
        params.set("perPage", "24");

        const response = await fetch(`/a/app?${params.toString()}`, {
          method: "GET",
          cache: "no-store",
        });
        const payload = await response.json();
        if (!response.ok || payload?.success === false) {
          throw new Error(payload?.error || "Failed to load league sessions");
        }

        const sessions = Array.isArray(payload?.sessions) ? payload.sessions : [];
        setSessionOptions(sessions);

        const selectedFromForm = String(formData.sessionId || "").trim();
        const selectedExists = selectedFromForm && sessions.some(function (session) {
          return String(session?.id || "") === selectedFromForm;
        });
        const nextSelectedId = selectedExists
          ? selectedFromForm
          : String(sessions[0]?.id || "").trim();

        if (nextSelectedId) {
          await loadSessionDetail(nextSelectedId);
        }
      } catch (error) {
        setSessionError(String(error?.message || "Failed to load sessions"));
      } finally {
        setSessionsLoading(false);
      }
    };

    useEffect(
      function () {
        if (!formData.id) return;
        fetchLeagueSessions(formData.id);
      },
      [formData.id]
    );

    const cloneFromSelectedSession = function () {
      const currentSessionId = String(formData.sessionId || "").trim();
      if (!currentSessionId) {
        setSessionError("Select a session first to duplicate it.");
        return;
      }
      setSessionError("");
      updateField("sourceSessionId", currentSessionId);
      updateField("sessionId", "");
      if (!String(formData.sessionTitle || "").trim()) {
        updateField("sessionTitle", "Copy of Session");
      }
    };

    const reorderGallery = function (fromIndex, toIndex) {
      if (fromIndex === toIndex) return;
      if (fromIndex < 0 || toIndex < 0) return;

      const newPreviews = Array.isArray(galleryPreviews) ? galleryPreviews.slice() : [];
      const newFileIds = Array.isArray(formData.galleryFileIds) ? formData.galleryFileIds.slice() : [];
      const newUrls = Array.isArray(formData.galleryUrls) ? formData.galleryUrls.slice() : [];

      if (fromIndex >= newPreviews.length || toIndex >= newPreviews.length) return;

      const [movedPreview] = newPreviews.splice(fromIndex, 1);
      const [movedFileId] = newFileIds.splice(fromIndex, 1);
      const [movedUrl] = newUrls.splice(fromIndex, 1);

      newPreviews.splice(toIndex, 0, movedPreview);
      newFileIds.splice(toIndex, 0, movedFileId);
      newUrls.splice(toIndex, 0, movedUrl);

      setGalleryPreviews(newPreviews);
      updateField("galleryFileIds", newFileIds);
      updateField("galleryUrls", newUrls);
    };

    const handleGallerySelect = async function (event) {
      const files = event?.target?.files;
      if (!files || !files.length) return;

      const fileToDataUrl = function (file) {
        return new Promise(function (resolve, reject) {
          const reader = new FileReader();
          reader.onload = function (e) {
            resolve(String(e?.target?.result || ""));
          };
          reader.onerror = function () {
            reject(new Error("Failed to read file"));
          };
          reader.readAsDataURL(file);
        });
      };

      const currentCount = Array.isArray(galleryPreviews) ? galleryPreviews.length : 0;
      const maxAllowed = Math.max(0, 20 - currentCount);
      const selectedFiles = Array.from(files).slice(0, maxAllowed);
      if (!selectedFiles.length) {
        event.target.value = "";
        return;
      }

      const resolveShopDomain = function () {
        try {
          const url = new URL(window.location.href);
          const fromQuery = String(url.searchParams.get("shop") || "").trim();
          if (fromQuery) return fromQuery;
        } catch (_error) {
          // ignore
        }
        const fromShopifyGlobal = String((window.Shopify && window.Shopify.shop) || "").trim();
        if (fromShopifyGlobal) return fromShopifyGlobal;
        return "";
      };

      const shopDomain = resolveShopDomain();
      setUploadError("");
      setPendingUploads(selectedFiles.length);

      const nextPreviews = Array.isArray(galleryPreviews) ? galleryPreviews.slice() : [];
      const nextFileIds = Array.isArray(formData.galleryFileIds) ? formData.galleryFileIds.slice() : [];
      const nextUrls = Array.isArray(formData.galleryUrls) ? formData.galleryUrls.slice() : [];

      try {
        for (const file of selectedFiles) {
          const previewUrl = await fileToDataUrl(file);
          const fileBase64 = previewUrl.includes(",") ? previewUrl.split(",").pop() : previewUrl;
          const query = new URLSearchParams({ app: "uploadFileToShopify" });
          if (shopDomain) query.set("shop", shopDomain);

          const response = await fetch(`/a/app?${query.toString()}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileBase64: String(fileBase64 || ""),
              fileName: String(file?.name || "upload-image"),
              mimeType: String(file?.type || "image/jpeg"),
              shopDomain: shopDomain || undefined,
            }),
          });

          const payload = await response.json();
          if (!response.ok) {
            throw new Error(String(payload?.message || payload?.error || "Upload failed"));
          }

          const gid = String(payload?.gid || payload?.fileId || "").trim();
          const url = String(payload?.url || "").trim();
          if (!gid) {
            throw new Error("Upload succeeded but no file ID was returned");
          }

          nextPreviews.push(previewUrl);
          nextFileIds.push(gid);
          nextUrls.push(url || previewUrl);

          setGalleryPreviews(nextPreviews.slice());
          updateField("galleryFileIds", nextFileIds.slice());
          updateField("galleryUrls", nextUrls.slice());
          setUploadError("");
          setPendingUploads(function (value) {
            return Math.max(0, value - 1);
          });
        }
      } catch (error) {
        setUploadError(String(error?.message || "Failed to upload image to Shopify"));
        setPendingUploads(0);
      }

      event.target.value = "";
    };

    const removeGalleryImage = function (index) {
      const nextPreviews = (Array.isArray(galleryPreviews) ? galleryPreviews : []).filter(function (_value, itemIndex) {
        return itemIndex !== index;
      });
      const nextFileIds = (Array.isArray(formData.galleryFileIds) ? formData.galleryFileIds : []).filter(function (_value, itemIndex) {
        return itemIndex !== index;
      });
      const nextUrls = (Array.isArray(formData.galleryUrls) ? formData.galleryUrls : []).filter(function (_value, itemIndex) {
        return itemIndex !== index;
      });
      setGalleryPreviews(nextPreviews);
      updateField("galleryFileIds", nextFileIds);
      updateField("galleryUrls", nextUrls);
    };

    const buildPayload = function (options) {
      const includeStatus = Boolean(options?.includeStatus);
      const explicitStatus = includeStatus
        ? normalizeInternalStatus(options?.status || formData.status)
        : undefined;

      const divisions = Array.isArray(formData.divisions)
        ? formData.divisions.map(function (division, index) {
          const days = Array.isArray(division?.days)
            ? division.days.map(function (day) { return String(day || "").trim().toLowerCase(); }).filter(Boolean)
            : [];
          return {
            id: String(division?.id || `division-${index + 1}`),
            name: String(division?.name || "").trim(),
            description: String(division?.description || "").trim(),
            rulesText: String(division?.rulesText || "").trim(),
            rulesFile: normalizeRulesFile(division?.rulesFile),
            annualMembershipFee: String(division?.annualMembershipFee || "").trim(),
            matchFee: String(division?.matchFee || "").trim(),
            days,
            startTime: String(division?.startTime || "").trim(),
            endTime: String(division?.endTime || "").trim(),
            locationIds: Array.isArray(division?.locationIds)
              ? division.locationIds.map(function (id) { return String(id || "").trim(); }).filter(Boolean)
              : [],
            teamIds: Array.isArray(division?.teamIds)
              ? division.teamIds.map(function (id) { return String(id || "").trim(); }).filter(Boolean)
              : [],
            teams: Array.isArray(division?.teams) ? division.teams : [],
            teamMemberResults: Array.isArray(division?.teamMemberResults) ? division.teamMemberResults : [],
            schedule: Array.isArray(division?.schedule) ? division.schedule : [],
          };
        })
        : [];

      const divisionLocationIds = Array.from(
        new Set(
          divisions.flatMap(function (division) {
            return Array.isArray(division.locationIds) ? division.locationIds : [];
          })
        )
      );

      const mergedLocationIds = Array.from(
        new Set((Array.isArray(formData.locationIds) ? formData.locationIds : []).concat(divisionLocationIds))
      );

      return {
        leagueId: formData.id || initialData?.id || undefined,
        title: formData.title,
        description: formData.description,
        isPublished: isGlobalCustomer ? Boolean(formData.isPublished) : false,
        status: isGlobalCustomer && includeStatus ? explicitStatus : undefined,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        websiteUrl: formData.websiteUrl,
        profileTypes: formData.profileTypes,
        annualMembershipFee: formData.annualMembershipFee,
        matchFee: formData.matchFee,
        defaultDescription: formData.defaultDescription,
        defaultAnnualMembershipFee: formData.defaultAnnualMembershipFee,
        defaultMatchFee: formData.defaultMatchFee,
        defaultDays: Array.isArray(formData.defaultDays) ? formData.defaultDays : [],
        defaultStartTime: formData.defaultStartTime,
        defaultEndTime: formData.defaultEndTime,
        defaultRulesText: formData.defaultRulesText,
        defaultRulesFile: normalizeRulesFile(formData.defaultRulesFile),
        sessionId: String(formData.sessionId || "").trim() || undefined,
        sourceSessionId: String(formData.sourceSessionId || "").trim() || undefined,
        sessionTitle: String(formData.sessionTitle || "").trim() || undefined,
        sessionStatus: String(formData.sessionStatus || "").trim() || undefined,
        sessionStartDate: String(formData.sessionStartDate || "").trim() || undefined,
        sessionEndDate: String(formData.sessionEndDate || "").trim() || undefined,
        divisions,
        galleryFileIds: formData.galleryFileIds,
        galleryUrls: formData.galleryUrls,
        locationIds: mergedLocationIds,
        specificationData: {
          leagues: {
            game_types: formData.gameTypes,
          },
        },
      };
    };

    const saveWithoutStatusChange = function () {
      if (isSaving) return;
      setShowStatusConfirm(false);
      onSubmit(buildPayload({ includeStatus: false }));
    };

    const saveWithStatusChange = function () {
      if (isSaving) return;
      setShowStatusConfirm(false);
      onSubmit(buildPayload({ includeStatus: true, status: confirmStatusValue }));
    };

    const submit = function () {
      if (isSaving) return;
      if (pendingUploads > 0) return;
      if (!String(formData.title || "").trim()) return;
      if (!String(formData.sessionStartDate || "").trim() || !String(formData.sessionEndDate || "").trim()) {
        setSessionError("Session start and end dates are required.");
        return;
      }
      setSessionError("");

      if (isGlobalCustomer && isEditingLeague) {
        setConfirmStatusValue(normalizeInternalStatus(formData.status));
        setShowStatusConfirm(true);
        return;
      }

      onSubmit(buildPayload({ includeStatus: false }));
    };

    const renderStepOne = function () {
      return h(
        "div",
        { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));" },
        isGlobalCustomer
          ? h(
            "div",
            {
              style: "grid-column: 1 / -1; display:inline-flex; align-items:center; gap:6px; padding:4px 10px; border-radius:999px; background:#e0f2fe; color:#0369a1; font-size:12px; font-weight:600; width:max-content; margin: -4px 0 6px;",
            },
            "Global user"
          )
          : null,
        isGlobalCustomer && isEditingLeague
          ? h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Internal Status"),
            h(
              "select",
              {
                className: "hd-input",
                value: formData.status || "pending_review",
                onChange: function (event) {
                  updateField("status", event.target.value || "pending_review");
                },
              },
              h("option", { value: "pending_review" }, "Pending review"),
              h("option", { value: "verified" }, "Verified"),
              h("option", { value: "certified" }, "Certified"),
              h("option", { value: "deleted" }, "Deleted")
            )
          )
          : null,
        isGlobalCustomer
          ? h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Published"),
            h(
              "label",
              { className: "hd-checkbox", style: "margin-top: 8px;" },
              h("input", {
                type: "checkbox",
                checked: Boolean(formData.isPublished),
                onChange: function (event) {
                  updateField("isPublished", Boolean(event.target.checked));
                },
              }),
              " Published"
            )
          )
          : null,
        h(
          "div",
          { className: "hd-field", style: "grid-column: 1 / -1;" },
          h("label", { className: "hd-label" }, "League Title"),
          h("input", {
            className: "hd-input",
            value: formData.title,
            placeholder: "Weekly 8-Ball League",
            onInput: function (event) {
              updateField("title", event.target.value);
            },
          })
        ),
        h(
          "div",
          { className: "hd-field", style: "grid-column: 1 / -1;" },
          h("label", { className: "hd-label" }, "Description"),
          h("textarea", {
            className: "hd-input hd-textarea",
            rows: 3,
            value: formData.description,
            placeholder: "Share league format, skill range, and signup details.",
            onInput: function (event) {
              updateField("description", event.target.value);
            },
          })
        ),
        h(
          "div",
          { className: "hd-field" },
          h("label", { className: "hd-label" }, "Contact Name"),
          h("input", {
            className: "hd-input",
            value: formData.contactName,
            onInput: function (event) {
              updateField("contactName", event.target.value);
            },
          })
        ),
        h(
          "div",
          { className: "hd-field" },
          h("label", { className: "hd-label" }, "Contact Email"),
          h("input", {
            className: "hd-input",
            type: "email",
            value: formData.contactEmail,
            onInput: function (event) {
              updateField("contactEmail", event.target.value);
            },
          })
        ),
        h(
          "div",
          { className: "hd-field" },
          h("label", { className: "hd-label" }, "Contact Phone"),
          h("input", {
            className: "hd-input",
            value: formData.contactPhone,
            onInput: function (event) {
              updateField("contactPhone", event.target.value);
            },
          })
        ),
        h(
          "div",
          { className: "hd-field" },
          h("label", { className: "hd-label" }, "Website"),
          h("input", {
            className: "hd-input",
            value: formData.websiteUrl,
            onInput: function (event) {
              updateField("websiteUrl", event.target.value);
            },
          })
        ),
        !isGlobalCustomer
          ? h(
            "div",
            { className: "hd-save-status", style: "margin-top: 10px;" },
            "This league will be saved as draft pending global review."
          )
          : null
      );
    };

    const renderStepTwo = function () {
      const jumpToSection = function (sectionId, tabKey) {
        setDetailsTab(tabKey);
        const element = document.getElementById(sectionId);
        if (element && typeof element.scrollIntoView === "function") {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };

      return h(
        "div",
        null,
        h(
          "div",
          { className: "hd-tabs", style: "margin-bottom: 12px;" },
          h(
            "button",
            {
              type: "button",
              className: "hd-tab" + (detailsTab === "session" ? " is-active" : ""),
              onClick: function () {
                jumpToSection("league-session-section", "session");
              },
            },
            "Session"
          ),
          h(
            "button",
            {
              type: "button",
              className: "hd-tab" + (detailsTab === "division" ? " is-active" : ""),
              onClick: function () {
                jumpToSection("league-divisions-section", "division");
              },
            },
            "Divisions"
          ),
          h(
            "button",
            {
              type: "button",
              className: "hd-tab" + (detailsTab === "teams" ? " is-active" : ""),
              onClick: function () {
                jumpToSection("league-teams-section", "teams");
              },
            },
            "Teams"
          )
        ),
        h(
          "div",
          { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));" },
          h(
            "div",
            { id: "league-session-section", className: "hd-field", style: "grid-column: 1 / -1; border:1px solid #e5e7eb; border-radius:10px; padding:12px; background:#f8fafc;" },
            h("h4", { style: "margin:0 0 8px;" }, "Session"),
            h(
              "div",
              { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));" },
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h("label", { className: "hd-label" }, "Existing Session"),
                h(
                  "div",
                  { style: "display:flex; gap:8px; align-items:center;" },
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: String(formData.sessionId || ""),
                      disabled: sessionsLoading || !formData.id,
                      onChange: function (event) {
                        const nextId = String(event.target.value || "").trim();
                        updateField("sourceSessionId", "");
                        if (!nextId) {
                          updateField("sessionId", "");
                          return;
                        }
                        loadSessionDetail(nextId).catch(function (error) {
                          setSessionError(String(error?.message || "Failed to load session detail"));
                        });
                      },
                    },
                    h("option", { value: "" }, formData.id ? "Create new session" : "Create league first"),
                    sessionOptions.map(function (session) {
                      const sessionId = String(session?.id || "");
                      const start = String(session?.startDate || "").slice(0, 10);
                      const end = String(session?.endDate || "").slice(0, 10);
                      const status = String(session?.status || "planned");
                      const label = String(session?.title || "Session")
                        + (start || end ? ` (${start || "?"} - ${end || "?"})` : "")
                        + ` [${status}]`;
                      return h("option", { key: sessionId, value: sessionId }, label);
                    })
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-cancel-btn",
                      disabled: sessionsLoading || !formData.id,
                      onClick: function () {
                        fetchLeagueSessions(formData.id);
                      },
                    },
                    sessionsLoading ? "Loading..." : "Reload"
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: !String(formData.sessionId || "").trim(),
                      onClick: cloneFromSelectedSession,
                    },
                    "Duplicate Session"
                  )
                )
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Session Title"),
                h("input", {
                  className: "hd-input",
                  value: formData.sessionTitle || "",
                  onInput: function (event) {
                    updateField("sessionTitle", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Session Status"),
                h(
                  "select",
                  {
                    className: "hd-input",
                    value: formData.sessionStatus || "planned",
                    onChange: function (event) {
                      updateField("sessionStatus", event.target.value || "planned");
                    },
                  },
                  h("option", { value: "planned" }, "Planned"),
                  h("option", { value: "active" }, "Active"),
                  h("option", { value: "completed" }, "Completed"),
                  h("option", { value: "archived" }, "Archived")
                )
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Session Start Date"),
                h("input", {
                  className: "hd-input",
                  type: "date",
                  value: formData.sessionStartDate || "",
                  onInput: function (event) {
                    updateField("sessionStartDate", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Session End Date"),
                h("input", {
                  className: "hd-input",
                  type: "date",
                  value: formData.sessionEndDate || "",
                  onInput: function (event) {
                    updateField("sessionEndDate", event.target.value);
                  },
                })
              )
            ),
            sessionError
              ? h("div", { style: "margin-top:8px; color:#b91c1c; font-size:12px;" }, sessionError)
              : null,
            String(formData.sourceSessionId || "").trim()
              ? h("div", { style: "margin-top:8px; color:#0369a1; font-size:12px;" }, "This save will duplicate the selected source session into a new session.")
              : null
          ),
          h(
            "div",
            { id: "league-teams-section", className: "hd-field", style: "grid-column: 1 / -1; border:1px solid #e5e7eb; border-radius:10px; padding:12px; background:#f8fafc;" },
            h("h4", { style: "margin:0 0 8px;" }, "Teams"),
            h(
              "div",
              { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));" },
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Search Teams"),
                h("input", {
                  className: "hd-input",
                  value: teamSearchQuery,
                  placeholder: "Search by team name",
                  onInput: function (event) {
                    setTeamSearchQuery(event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "New Team Name"),
                h("input", {
                  className: "hd-input",
                  value: teamNameInput,
                  placeholder: "Team Alpha",
                  onInput: function (event) {
                    setTeamNameInput(event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h("label", { className: "hd-label" }, "Members (comma-separated)"),
                h("input", {
                  className: "hd-input",
                  value: teamMembersInput,
                  placeholder: "Alice, Bob, Carol",
                  onInput: function (event) {
                    setTeamMembersInput(event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h(
                  "button",
                  {
                    type: "button",
                    className: "hd-save-btn",
                    disabled: creatingTeam || !String(formData.id || "").trim(),
                    onClick: createLeagueTeam,
                  },
                  creatingTeam ? "Creating..." : "Add Team"
                )
              )
            ),
            teamsError
              ? h("div", { style: "margin-top:8px; color:#b91c1c; font-size:12px;" }, teamsError)
              : null,
            h(
              "div",
              {
                className: "hd-table-wrap",
                style: "max-height: 200px; overflow:auto; border:1px solid #e5e7eb; border-radius:8px; background:#fff; margin-top:8px;",
              },
              teamsLoading
                ? h("div", { className: "hd-empty", style: "padding: 10px;" }, "Loading teams...")
                : leagueTeams.length
                  ? leagueTeams.map(function (team) {
                    const memberCount = Array.isArray(team?.members) ? team.members.length : 0;
                    return h(
                      "div",
                      { key: String(team?.id || ""), style: "padding:8px 10px; border-bottom:1px solid #f3f4f6;" },
                      h("strong", null, String(team?.name || "Unnamed Team")),
                      h("div", { style: "font-size:12px; color:#6b7280;" }, `${memberCount} member${memberCount === 1 ? "" : "s"}`)
                    );
                  })
                  : h("div", { className: "hd-empty", style: "padding: 10px;" }, "No teams yet")
            )
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Profile Types (comma-separated)"),
            h("input", {
              className: "hd-input",
              value: formData.profileTypes.join(", "),
              placeholder: "bar, poolhall, restaurant",
              onInput: function (event) {
                updateField("profileTypes", parseCsv(event.target.value).map(function (v) { return v.toLowerCase(); }));
              },
            })
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Game Types (comma-separated)"),
            h("input", {
              className: "hd-input",
              value: formData.gameTypes.join(", "),
              placeholder: "8-ball, 9-ball, straight pool",
              onInput: function (event) {
                updateField("gameTypes", parseCsv(event.target.value));
              },
            })
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Annual Membership Fee"),
            h("input", {
              className: "hd-input",
              type: "number",
              min: "0",
              step: "0.01",
              value: formData.annualMembershipFee,
              onInput: function (event) {
                updateField("annualMembershipFee", event.target.value);
              },
            })
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Match Fee"),
            h("input", {
              className: "hd-input",
              type: "number",
              min: "0",
              step: "0.01",
              value: formData.matchFee,
              onInput: function (event) {
                updateField("matchFee", event.target.value);
              },
            })
          ),
          h(
            "div",
            { className: "hd-field", style: "grid-column: 1 / -1;" },
            h("h4", { style: "margin: 4px 0 8px;" }, "Division Defaults"),
            h("div", { style: "font-size: 12px; color: #6b7280; margin-bottom: 8px;" }, "Used when creating divisions and can be overridden per division."),
            h(
              "div",
              { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));" },
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h("label", { className: "hd-label" }, "Default Description"),
                h("textarea", {
                  className: "hd-input hd-textarea",
                  rows: 2,
                  value: formData.defaultDescription,
                  onInput: function (event) {
                    updateField("defaultDescription", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h("label", { className: "hd-label" }, "Default Rules Text"),
                h("textarea", {
                  className: "hd-input hd-textarea",
                  rows: 3,
                  value: formData.defaultRulesText || "",
                  onInput: function (event) {
                    updateField("defaultRulesText", event.target.value);
                  },
                }),
                h(
                  "div",
                  { style: "display:flex; gap:8px; align-items:center; margin-top:8px;" },
                  h("input", {
                    className: "hd-input",
                    type: "file",
                    accept: "application/pdf",
                    onChange: uploadRulesFile(function (uploaded) {
                      updateField("defaultRulesFile", uploaded);
                    }),
                    disabled: pendingUploads > 0,
                  }),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-cancel-btn",
                      onClick: function () {
                        updateField("defaultRulesFile", {});
                      },
                    },
                    "Clear"
                  )
                ),
                formData.defaultRulesFile && (formData.defaultRulesFile.fileName || formData.defaultRulesFile.url)
                  ? h(
                    "div",
                    { style: "margin-top:6px; font-size:12px; color:#4b5563;" },
                    "PDF: ",
                    formData.defaultRulesFile.fileName || formData.defaultRulesFile.url
                  )
                  : null
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Default Annual Membership Fee"),
                h("input", {
                  className: "hd-input",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  value: formData.defaultAnnualMembershipFee,
                  onInput: function (event) {
                    updateField("defaultAnnualMembershipFee", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Default Match Fee"),
                h("input", {
                  className: "hd-input",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  value: formData.defaultMatchFee,
                  onInput: function (event) {
                    updateField("defaultMatchFee", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Default Start Time"),
                h("input", {
                  className: "hd-input",
                  type: "time",
                  value: formData.defaultStartTime,
                  onInput: function (event) {
                    updateField("defaultStartTime", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field" },
                h("label", { className: "hd-label" }, "Default End Time"),
                h("input", {
                  className: "hd-input",
                  type: "time",
                  value: formData.defaultEndTime,
                  onInput: function (event) {
                    updateField("defaultEndTime", event.target.value);
                  },
                })
              ),
              h(
                "div",
                { className: "hd-field", style: "grid-column: 1 / -1;" },
                h("label", { className: "hd-label" }, "Default Days"),
                h(
                  "div",
                  { style: "display:flex; flex-wrap:wrap; gap:10px; margin-top:6px;" },
                  DAY_OPTIONS.map(function (day) {
                    const checked = Array.isArray(formData.defaultDays) && formData.defaultDays.includes(day);
                    return h(
                      "label",
                      { key: `default-day-${day}`, className: "hd-checkbox", style: "display:inline-flex; gap:6px; align-items:center;" },
                      h("input", {
                        type: "checkbox",
                        checked,
                        onChange: function () {
                          toggleDaySelection("defaultDays", day);
                        },
                      }),
                      day.charAt(0).toUpperCase() + day.slice(1)
                    );
                  })
                )
              )
            )
          ),
          h(
            "div",
            { id: "league-divisions-section", className: "hd-field", style: "grid-column: 1 / -1;" },
            h(
              "div",
              { style: "display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;" },
              h("h4", { style: "margin: 0;" }, "Divisions"),
              h(
                "button",
                {
                  type: "button",
                  className: "hd-save-btn",
                  onClick: addDivision,
                },
                "Add Division"
              )
            ),
            (Array.isArray(formData.divisions) && formData.divisions.length > 0)
              ? formData.divisions.map(function (division, divisionIndex) {
                const divisionLocations = Array.isArray(division.locationIds) ? division.locationIds : [];
                const divisionKey = String(division.id || `division-${divisionIndex}`);
                const locationQueryValue = String(divisionLocationQueries[divisionKey] || "").trim().toLowerCase();
                const divisionLocationOptions = (Array.isArray(profiles) ? profiles : []).filter(function (profile) {
                  if (!locationQueryValue) return true;
                  const title = String(profile?.title || "").toLowerCase();
                  const city = String(profile?.locationCity || "").toLowerCase();
                  const state = String(getStateDisplayName(profile?.locationState) || "").toLowerCase();
                  const country = String(profile?.locationCountry || "").toLowerCase();
                  return title.includes(locationQueryValue)
                    || city.includes(locationQueryValue)
                    || state.includes(locationQueryValue)
                    || country.includes(locationQueryValue);
                });
                return h(
                  "div",
                  {
                    key: division.id || `division-${divisionIndex}`,
                    style: "border:1px solid #e5e7eb; border-radius:10px; padding:12px; margin-bottom:10px; background:#fff;",
                  },
                  h(
                    "div",
                    { style: "display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;" },
                    h("strong", null, division.name || `Division ${divisionIndex + 1}`),
                    h(
                      "button",
                      {
                        type: "button",
                        className: "hd-cancel-btn",
                        onClick: function () {
                          removeDivision(divisionIndex);
                        },
                      },
                      "Remove"
                    )
                  ),
                  h(
                    "div",
                    { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));" },
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Division Name"),
                      h("input", {
                        className: "hd-input",
                        value: division.name || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "name", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Division Description"),
                      h("textarea", {
                        className: "hd-input hd-textarea",
                        rows: 2,
                        value: division.description || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "description", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Division Rules Text"),
                      h("textarea", {
                        className: "hd-input hd-textarea",
                        rows: 3,
                        value: division.rulesText || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "rulesText", event.target.value);
                        },
                      }),
                      h(
                        "div",
                        { style: "display:flex; gap:8px; align-items:center; margin-top:8px;" },
                        h("input", {
                          className: "hd-input",
                          type: "file",
                          accept: "application/pdf",
                          onChange: uploadRulesFile(function (uploaded) {
                            updateDivisionField(divisionIndex, "rulesFile", uploaded);
                          }),
                          disabled: pendingUploads > 0,
                        }),
                        h(
                          "button",
                          {
                            type: "button",
                            className: "hd-cancel-btn",
                            onClick: function () {
                              updateDivisionField(divisionIndex, "rulesFile", {});
                            },
                          },
                          "Clear"
                        )
                      ),
                      division.rulesFile && (division.rulesFile.fileName || division.rulesFile.url)
                        ? h(
                          "div",
                          { style: "margin-top:6px; font-size:12px; color:#4b5563;" },
                          "PDF: ",
                          division.rulesFile.fileName || division.rulesFile.url
                        )
                        : null
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Assigned Teams"),
                      h(
                        "div",
                        { style: "font-size:12px; color:#6b7280; margin-bottom:6px;" },
                        "Select teams from the Teams tab. Team assignments copy forward on session duplication."
                      ),
                      h(
                        "div",
                        {
                          className: "hd-table-wrap",
                          style: "max-height: 180px; overflow:auto; border:1px solid #e5e7eb; border-radius:8px; background:#fff;",
                        },
                        leagueTeams.length
                          ? leagueTeams.map(function (team) {
                            const teamId = String(team?.id || "");
                            const checked = Array.isArray(division.teamIds) && division.teamIds.includes(teamId);
                            const memberCount = Array.isArray(team?.members) ? team.members.length : 0;
                            return h(
                              "label",
                              {
                                key: `${division.id}-team-${teamId}`,
                                className: "hd-checkbox",
                                style: "display:flex; gap:8px; padding:8px 10px; border-bottom:1px solid #f3f4f6; align-items:flex-start;",
                              },
                              h("input", {
                                type: "checkbox",
                                checked,
                                onChange: function () {
                                  toggleDivisionTeamId(divisionIndex, teamId);
                                },
                              }),
                              h(
                                "span",
                                null,
                                h("strong", null, String(team?.name || "Unnamed Team")),
                                h("div", { style: "font-size:12px; color:#6b7280;" }, `${memberCount} member${memberCount === 1 ? "" : "s"}`)
                              )
                            );
                          })
                          : h("div", { className: "hd-empty", style: "padding: 10px;" }, "No teams available. Add teams in the Teams tab.")
                      )
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Session Schedule (JSON array)"),
                      h("textarea", {
                        className: "hd-input hd-textarea",
                        rows: 4,
                        value: toPrettyJson(division.schedule),
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "schedule", parseJsonArray(event.target.value));
                        },
                      }),
                      h(
                        "div",
                        { style: "font-size:12px; color:#6b7280; margin-top:6px;" },
                        "Schedule is session-specific and resets when duplicating to a new session."
                      )
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Team / Member Results (JSON array)"),
                      h("textarea", {
                        className: "hd-input hd-textarea",
                        rows: 4,
                        value: toPrettyJson(division.teamMemberResults),
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "teamMemberResults", parseJsonArray(event.target.value));
                        },
                      }),
                      h(
                        "div",
                        { style: "font-size:12px; color:#6b7280; margin-top:6px;" },
                        "Results are session-specific and do not copy to new sessions."
                      )
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "Annual Membership Fee"),
                      h("input", {
                        className: "hd-input",
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: division.annualMembershipFee || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "annualMembershipFee", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "Match Fee"),
                      h("input", {
                        className: "hd-input",
                        type: "number",
                        min: "0",
                        step: "0.01",
                        value: division.matchFee || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "matchFee", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "Start Time"),
                      h("input", {
                        className: "hd-input",
                        type: "time",
                        value: division.startTime || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "startTime", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field" },
                      h("label", { className: "hd-label" }, "End Time"),
                      h("input", {
                        className: "hd-input",
                        type: "time",
                        value: division.endTime || "",
                        onInput: function (event) {
                          updateDivisionField(divisionIndex, "endTime", event.target.value);
                        },
                      })
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Days"),
                      h(
                        "div",
                        { style: "display:flex; flex-wrap:wrap; gap:10px; margin-top:6px;" },
                        DAY_OPTIONS.map(function (day) {
                          const checked = Array.isArray(division.days) && division.days.includes(day);
                          return h(
                            "label",
                            { key: `${division.id}-day-${day}`, className: "hd-checkbox", style: "display:inline-flex; gap:6px; align-items:center;" },
                            h("input", {
                              type: "checkbox",
                              checked,
                              onChange: function () {
                                toggleDivisionDay(divisionIndex, day);
                              },
                            }),
                            day.charAt(0).toUpperCase() + day.slice(1)
                          );
                        })
                      )
                    ),
                    h(
                      "div",
                      { className: "hd-field", style: "grid-column: 1 / -1;" },
                      h("label", { className: "hd-label" }, "Division Locations"),
                      h("input", {
                        className: "hd-input",
                        value: divisionLocationQueries[divisionKey] || "",
                        placeholder: "Search locations by name, city, state...",
                        onInput: function (event) {
                          setDivisionLocationQuery(divisionKey, event.target.value);
                        },
                        style: "margin-bottom:8px;",
                      }),
                      h(
                        "div",
                        { style: "font-size:12px; color:#6b7280; margin-bottom:6px;" },
                        divisionLocations.length,
                        " location",
                        divisionLocations.length === 1 ? "" : "s",
                        " selected"
                      ),
                      h(
                        "div",
                        {
                          className: "hd-table-wrap",
                          style: "max-height: 180px; overflow:auto; border:1px solid #e5e7eb; border-radius:8px; background:#fff;",
                        },
                        divisionLocationOptions.length
                          ? divisionLocationOptions.map(function (profile) {
                            const profileId = String(profile?.id || "");
                            const checked = divisionLocations.includes(profileId);
                            const locationText = [profile?.locationCity, getStateDisplayName(profile?.locationState)]
                              .filter(Boolean)
                              .join(", ");
                            return h(
                              "label",
                              {
                                key: `${division.id}-location-${profileId}`,
                                className: "hd-checkbox",
                                style: "display:flex; gap:8px; padding:8px 10px; border-bottom:1px solid #f3f4f6; align-items:flex-start;",
                              },
                              h("input", {
                                type: "checkbox",
                                checked,
                                onChange: function () {
                                  toggleDivisionLocation(divisionIndex, profileId);
                                },
                              }),
                              h(
                                "span",
                                null,
                                h("strong", null, profile?.title || "Untitled"),
                                h(
                                  "div",
                                  { style: "font-size:12px; color:#6b7280;" },
                                  locationText || "No location metadata"
                                )
                              )
                            );
                          })
                          : h("div", { className: "hd-empty", style: "padding: 10px;" }, "No matching locations")
                      )
                    )
                  )
                );
              })
              : h("div", { className: "hd-empty", style: "padding: 10px;" }, "No divisions yet. Add one to group locations.")
          ),
          h(
            "div",
            { className: "hd-field", style: "grid-column: 1 / -1;" },
            h("label", { className: "hd-label" }, "League Gallery (max 20)"),
            h("div", { style: "margin-bottom: 6px; font-size: 12px; color: #6b7280;" }, "The first image is used on league cards. Drag images to reorder the gallery."),
            h("input", {
              className: "hd-input",
              type: "file",
              accept: "image/*",
              multiple: true,
              onChange: handleGallerySelect,
              disabled: pendingUploads > 0 || (Array.isArray(formData.galleryFileIds) ? formData.galleryFileIds.length : 0) >= 20,
            }),
            pendingUploads > 0
              ? h("div", { style: "margin-top: 6px; font-size: 12px; color: #6b7280;" }, "Uploading images...")
              : null,
            uploadError
              ? h("div", { style: "margin-top: 6px; font-size: 12px; color: #b91c1c;" }, uploadError)
              : null,
            Array.isArray(galleryPreviews) && galleryPreviews.length
              ? h(
                "div",
                { style: "display:flex; flex-wrap:wrap; gap:8px; margin-top:10px;" },
                galleryPreviews.map(function (url, index) {
                  return h(
                    "div",
                    {
                      key: `${url}-${index}`,
                      draggable: pendingUploads === 0,
                      onDragStart: function (event) {
                        if (pendingUploads > 0) return;
                        event.dataTransfer.setData("text/plain", String(index));
                      },
                      onDragOver: function (event) {
                        if (pendingUploads > 0) return;
                        event.preventDefault();
                      },
                      onDrop: function (event) {
                        if (pendingUploads > 0) return;
                        event.preventDefault();
                        const fromIndex = Number(event.dataTransfer.getData("text/plain"));
                        reorderGallery(fromIndex, index);
                      },
                      style: "position:relative; width:86px; height:86px; border-radius:8px; overflow:hidden; border:1px solid #e5e7eb; background:#f8fafc; cursor:" + (pendingUploads > 0 ? "default" : "grab") + ";",
                    },
                    h("img", {
                      src: url,
                      alt: `League image ${index + 1}`,
                      style: "width:100%; height:100%; object-fit:cover;",
                    }),
                    index === 0
                      ? h(
                        "div",
                        {
                          style: "position:absolute; left:4px; bottom:4px; padding:2px 6px; border-radius:999px; background:rgba(17,24,39,0.78); color:#fff; font-size:10px; font-weight:600;",
                        },
                        "Primary"
                      )
                      : null,
                    h(
                      "button",
                      {
                        type: "button",
                        onClick: function () {
                          removeGalleryImage(index);
                        },
                        disabled: pendingUploads > 0,
                        style: "position:absolute; top:4px; right:4px; border:0; border-radius:999px; width:20px; height:20px; cursor:pointer; background:rgba(17,24,39,0.75); color:#fff;",
                        "aria-label": `Remove image ${index + 1}`,
                      },
                      "x"
                    )
                  );
                })
              )
              : null
          )
        ),
        h("hr", { style: "margin: 16px 0; border: 0; border-top: 1px solid #e5e7eb;" }),
        h("h4", { style: "margin: 0 0 12px;" }, "Locations"),
        h(
          "div",
          { className: "hd-grid", style: "grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); margin-bottom: 12px;" },
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "Location Name"),
            h("input", {
              className: "hd-input",
              value: locationQuery,
              placeholder: "Search locations",
              onInput: function (event) {
                setLocationQuery(event.target.value);
              },
            })
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "State"),
            h("input", {
              className: "hd-input",
              list: "leagueLocationStateOptions",
              value: getStateDisplayName(locationStateFilter),
              onInput: function (event) {
                setLocationStateFilter(getStateAbbrev(event.target.value) || "");
              },
            }),
            h(
              "datalist",
              { id: "leagueLocationStateOptions" },
              locationStateOptions.map(function (stateName) {
                return h("option", { key: stateName, value: stateName });
              })
            )
          ),
          h(
            "div",
            { className: "hd-field" },
            h("label", { className: "hd-label" }, "City"),
            h("input", {
              className: "hd-input",
              list: "leagueLocationCityOptions",
              value: locationCityFilter,
              onInput: function (event) {
                setLocationCityFilter(event.target.value);
              },
            }),
            h(
              "datalist",
              { id: "leagueLocationCityOptions" },
              locationCityOptions.map(function (city) {
                return h("option", { key: city, value: city });
              })
            )
          )
        ),
        h(
          "div",
          {
            className: "hd-table-wrap",
            style: "max-height: 220px; overflow: auto; border: 1px solid #e5e7eb; border-radius: 8px;",
          },
          locationsLoading
            ? h("div", { className: "hd-empty", style: "padding: 12px;" }, "Loading locations...")
            : filteredProfiles.length
              ? filteredProfiles.map(function (profile) {
                const profileId = String(profile.id || "");
                const checked = formData.locationIds.includes(profileId);
                return h(
                  "label",
                  {
                    key: profileId,
                    className: "hd-checkbox",
                    style: "display:flex; gap:8px; padding:10px 12px; border-bottom:1px solid #f3f4f6; align-items:flex-start;",
                  },
                  h("input", {
                    type: "checkbox",
                    checked: checked,
                    onChange: function () {
                      toggleLocation(profileId);
                    },
                  }),
                  h(
                    "span",
                    null,
                    h("strong", null, profile.title || "Untitled"),
                    h(
                      "div",
                      { style: "font-size:12px; color:#6b7280;" },
                      [profile.locationCity, getStateDisplayName(profile.locationState)].filter(Boolean).join(", ")
                    )
                  )
                );
              })
              : h("div", { className: "hd-empty", style: "padding: 12px;" }, "No locations found")
        ),
        h(
          "div",
          { style: "margin-top: 8px; font-size: 12px; color: #6b7280;" },
          formData.locationIds.length,
          " location",
          formData.locationIds.length === 1 ? "" : "s",
          " selected"
        )
      );
    };

    return h(
      "div",
      { className: "hd-modal league-modal-root" },
      h("div", { className: "hd-modal__backdrop", onClick: isSaving ? undefined : onClose }),
      h(
        "button",
        {
          type: "button",
          className: "hd-modal__close",
          onClick: isSaving ? undefined : onClose,
          disabled: isSaving,
          "aria-label": "Close modal",
        },
        "×"
      ),
      h(
        "div",
        { className: "hd-modal__panel" },
        h(
          "div",
          { className: "hd-card hd-card--form" },
          h("h3", null, formData.id ? "Edit League" : "Add League"),
          h(
            "div",
            { className: "hd-tabs", style: "margin-bottom: 12px;" },
            h(
              "button",
              {
                type: "button",
                className: "hd-tab" + (step === 1 ? " is-active" : ""),
                onClick: function () {
                  setStep(1);
                },
              },
              "Basics"
            ),
            h(
              "button",
              {
                type: "button",
                className: "hd-tab" + (step === 2 ? " is-active" : ""),
                onClick: function () {
                  setStep(2);
                },
              },
              "Details"
            )
          ),
          step === 1 ? renderStepOne() : renderStepTwo(),
          saveStatus ? h("div", { className: "hd-save-status hd-save-status--banner", style: "margin-top: 10px;" }, saveStatus) : null,
          h(
            "div",
            { className: "hd-modal__actions", style: "margin-top: 16px; display: flex; gap: 8px; justify-content: flex-end;" },
            step > 1
              ? h(
                "button",
                {
                  type: "button",
                  className: "hd-cancel-btn",
                  disabled: isSaving || pendingUploads > 0,
                  onClick: function () {
                    setStep(step - 1);
                  },
                },
                "Back"
              )
              : null,
            step < 2
              ? h(
                "button",
                {
                  type: "button",
                  className: "hd-save-btn",
                  disabled: isSaving || pendingUploads > 0,
                  onClick: function () {
                    setStep(step + 1);
                  },
                },
                pendingUploads > 0 ? "Uploading..." : "Next"
              )
              : h(
                "button",
                {
                  type: "button",
                  className: "hd-save-btn",
                  disabled: isSaving || pendingUploads > 0,
                  onClick: submit,
                },
                pendingUploads > 0
                  ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Uploading images..."]
                  : isSaving
                    ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Saving..."]
                    : (formData.id ? "Save League" : "Create League")
              )
          ),
          showStatusConfirm && isEditingLeague
            ? h(
              "div",
              {
                style: "position:fixed; inset:0; background:rgba(17,24,39,0.5); z-index:10001; display:flex; align-items:center; justify-content:center;",
              },
              h(
                "div",
                {
                  className: "hd-card",
                  style: "width:min(440px, calc(100vw - 32px));",
                },
                h("h4", { style: "margin:0;" }, "Change Internal Status?"),
                h(
                  "p",
                  { className: "hd-card__meta", style: "margin:0;" },
                  "Do you want to update status before saving this league?"
                ),
                h(
                  "div",
                  { className: "hd-field" },
                  h("label", { className: "hd-label" }, "Internal Status"),
                  h(
                    "select",
                    {
                      className: "hd-input",
                      value: confirmStatusValue,
                      onChange: function (event) {
                        setConfirmStatusValue(event.target.value || "pending_review");
                      },
                    },
                    h("option", { value: "pending_review" }, "Pending review"),
                    h("option", { value: "verified" }, "Verified"),
                    h("option", { value: "certified" }, "Certified"),
                    h("option", { value: "deleted" }, "Deleted")
                  )
                ),
                h(
                  "div",
                  { style: "display:flex; justify-content:flex-end; gap:8px;" },
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-cancel-btn",
                      disabled: isSaving || pendingUploads > 0,
                      onClick: saveWithoutStatusChange,
                    },
                    "No"
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      className: "hd-save-btn",
                      disabled: isSaving || pendingUploads > 0,
                      onClick: saveWithStatusChange,
                    },
                    pendingUploads > 0
                      ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Uploading images..."]
                      : isSaving
                        ? [h("span", { className: "hd-spinner", "aria-hidden": "true" }), "Saving..."]
                        : "Save"
                  )
                )
              )
            )
            : null
        )
      )
    );
  };
})();
